import Details from '@components/hero-section/Details';
import HeroVideo from '@components/hero-section/HeroVideo';
import Title from '@components/hero-section/Title';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import {
  BrowserType,
  PlatformType,
} from '@invideoio/web-shared/constants/common';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { HeroSectionType } from '../../types/landing-page';

interface HeroSectionProps {
  heroSection: HeroSectionType;
  onCTAIntersection?: Function;
  allowEditor: boolean;
  hideEmailOnMobile?: boolean;
  onSignUp: Function;
  platform: PlatformType;
  browser: BrowserType;
}

const HeroSection = ({
  browser,
  heroSection,
  allowEditor,
  hideEmailOnMobile,
  onSignUp,
  platform,
}: HeroSectionProps) => {
  const {
    video,
    poster,
    heading,
    sideText,
    buttonText,
    CTAUrl,
    subText,
    MobileAppLink,
    showMobileAppCTA,
  } = heroSection;

  const router = useRouter();

  const tempalteData = useMemo(() => {
    const toolName = ivclass(
      heading.heading1,
      heading.heading2,
      heading.heading3,
      heading.heading4,
    );

    const toolDescription = ivclass(
      sideText.highlightedText,
      sideText?.plainText1,
      sideText?.plainText2,
    );

    return {
      toolName: toolName || '',
      toolCta: buttonText,
      toolDescription: toolDescription,
      toolImageUrl: poster.url,
      toolTargetURL: router.asPath,
    };
  }, [buttonText, heading, poster.url, sideText, router.asPath]);

  return (
    <React.Fragment>
      <section
        className={ivclass(
          'iv-pt-100 lg:iv-pt-80 md:iv-pt-75 sm:iv-pt-50 iv-px-20',
        )}
      >
        <div className={ivclass('iv-grid', 'iv-grid-cols-12')}>
          <div
            className={ivclass(
              'iv-col-span-12',
              'xl:iv-col-start-2 lg:iv-col-start-2',
              'xl:iv-pl-5 lg:iv-pl-20',
            )}
          >
            <Title heading={heading} />
          </div>
        </div>
      </section>

      <section
        className={ivclass(
          'iv-relative',
          'md:iv-mt-5',
          'xl:iv-pt-75 lg:iv-pt-50 md:iv-pt-50 sm:iv-pt-[33px] iv-px-20 lg:iv-pl-40',
        )}
      >
        <div className={ivclass('iv-grid', 'iv-grid-cols-12')}>
          <div
            className={ivclass(
              'xl:iv-col-start-2 lg:iv-col-start-2',
              'iv-col-span-4 md:iv-col-span-12 sm:iv-col-span-12',
              'xl:iv-pl-3',
              'md:iv-text-center sm:iv-text-center',
            )}
          >
            <Details
              sideText={sideText}
              buttonText={buttonText}
              CTAUrl={CTAUrl}
              subText={subText}
              allowEditor={allowEditor}
              hideEmailOnMobile={hideEmailOnMobile}
              MobileAppLink={MobileAppLink}
              tempalteData={tempalteData}
              onSignUp={onSignUp}
              showMobileAppCTA={Boolean(showMobileAppCTA)}
            />
          </div>
          <div
            className={ivclass(
              'md:iv-relative sm:iv-relative',
              'iv-col-span-7 md:iv-col-span-12 sm:iv-col-span-12',
              'xl:iv-w-[960px] lg:iv-w-[760px]',
              'xl:iv-h-[620px] lg:iv-h-[540px] md:iv-max-h-[600px] sm:iv-max-h-[355px]',
              'xl:-iv-mt-75 lg:-iv-mt-60',
            )}
          >
            <HeroVideo
              browser={browser}
              video={video}
              poster={poster}
              platform={platform}
            />
          </div>
        </div>
      </section>
    </React.Fragment>
  );
};

export default HeroSection;
